import { getGameGrid } from '../../../utils/game/spawnConfig';
import { computeGridPositions } from '../../../utils/game/grid';
import { playUI, UI } from '../../../utils/arena/uiSounds';
import { chooseIntent } from '../../../utils/game/net/automationIntents';
import { countsAsMinion, sampleCellGeometry } from '../../../utils/game/engine/core';
import {
  createHarbingerCircle,
  updateHarbingerCircleGeometry,
  disposeHarbingerCircle,
} from '../../../utils/game/visualEffects/harbingerCircle';
import {
  buildHarbingerLedgerEntry,
  marksFromLedgerEntry,
} from '../../../utils/game/engine/automation/ops/cards/harbinger';

// Harbinger pre-game ceremony + summoning-circle decals, extracted from
// GameBoard.jsx.
//
// Codex: "On setup, determine three random squares." The ceremony runs
// AFTER the first-player roll-off resolves and BEFORE any opening draw.
// installs the marks while the board is still empty so both players can
// see the chosen squares before the game proper begins.
//
// Sequence (for each Harbinger in play, going-first first):
//   1. Pop the next Harbinger; show the top banner labelled with
//      the avatar's owner. The playmat below stays fully visible.
//   2. THREE ROUNDS, one cell locked per round. Each round runs a
//      slot-machine cycle: a single highlight jumps cell-to-cell
//      every ~70ms, decelerating in the last quarter, then locks
//      onto the pre-picked mark for that round. Locked cells stay
//      visible while subsequent rounds run.
//   3. REVEAL hold (~600ms) once all three are locked.
//   4. COMMIT: install the MODIFY_CAST_COST ledger entry and clear
//      the sweep. The renderer reads from the ledger from this
//      point on, so the persistent circles take over.
//   5. Brief settle pause (~400ms), then move on to the next
//      Harbinger (if any) or fire performOpeningDraw.
//
// Auth (online AND solo): the ceremony is a durable server pendingChoice
// (harbingerSetup.js). Both seats animate the identical staged marks
// via foldHarbingerPromptFromMirror, and the ledger fold renders the
// installed circles. The local pick path below is legacy flag-off only.
//
// Host contract: props.isCasualSandbox, state (isHost, spawnConfig,
// harbingerCeremony, harbingerSweep, regionView), setState, sync,
// scene, effectLedger, harbingerCircleMeshes, _findAvatarForPlayer,
// _getFullCard, _unmounted, _viewTransition, plus the _harbinger*
// instance fields used as cross-call scratch state.

export function animateHarbingerCeremony(host, { avatar, marks, totalCols, totalRows, install, seat, authPromptId = null }) {
  // Slot-machine pacing. Three rounds, one cell locked per round.
  // Each round cycles a single highlight rapidly across cells, then
  // decelerates and lands on the round's pre-picked mark. After all
  // three locks, the ceremony enters pending_confirm. The owner's
  // client surfaces Accept / Cancel; Accept installs the ledger
  // entry, Cancel aborts the ability entirely for this match.
  const SWEEP_TICK_MS = 70;
  const SWEEP_DURATION_PER_PICK = 900;
  const LOCK_PAUSE_MS = 320;

  // Derive the player key from `seat` directly. Reading state here is
  // stale on the second Harbinger because the preceding setState
  // hasn't flushed yet; the sweep would then carry the previous
  // ceremony's colour.
  const playerKey = seat === 2 ? 'p2' : 'p1';
  const allCells = [];
  for (let r = 0; r < totalRows; r++) {
    for (let c = 0; c < totalCols; c++) {
      allCells.push({ col: c, row: r });
    }
  }

  let cancelled = false;
  host._harbingerSweepCancel?.();
  host._harbingerSweepCancel = () => { cancelled = true; };

  const locked = [];
  const setSweep = (sweepCell) => {
    if (cancelled || host._unmounted) return;
    try {
      host.setState({ harbingerSweep: { sweepCell, lockedCells: [...locked], playerKey } });
    } catch (err) {
      console.warn('[harbinger] setSweep failed:', err);
    }
  };
  setSweep(null);

  const runRound = (idx, onAllDone) => {
    if (cancelled || host._unmounted) return;
    const finalCell = marks[idx];
    if (!finalCell) { onAllDone(); return; }
    const startedAt = performance.now();
    let lastCell = null;

    const cycle = () => {
      if (cancelled || host._unmounted) return;
      const elapsed = performance.now() - startedAt;
      if (elapsed >= SWEEP_DURATION_PER_PICK) {
        locked.push(finalCell);
        setSweep(null);
        try { playUI(UI.CLICK_SOFT, { volume: 0.65 }); } catch {}
        setTimeout(() => {
          if (cancelled || host._unmounted) return;
          if (idx + 1 < marks.length) runRound(idx + 1, onAllDone);
          else onAllDone();
        }, LOCK_PAUSE_MS);
        return;
      }
      let pick = allCells[Math.floor(Math.random() * allCells.length)];
      for (let i = 0; i < 12; i++) {
        const collidesLocked = locked.some((l) => l.col === pick.col && l.row === pick.row);
        const sameAsLast = lastCell && lastCell.col === pick.col && lastCell.row === pick.row;
        if (!collidesLocked && !sameAsLast) break;
        pick = allCells[Math.floor(Math.random() * allCells.length)];
      }
      lastCell = pick;
      setSweep(pick);
      try { playUI(UI.HOVER, { volume: 0.32, debounce: 0 }); } catch {}
      const t = elapsed / SWEEP_DURATION_PER_PICK;
      const slow = t > 0.75 ? Math.pow((t - 0.75) / 0.25, 2) * 220 : 0;
      setTimeout(cycle, SWEEP_TICK_MS + slow);
    };
    cycle();
  };

  runRound(0, () => {
    if (cancelled || host._unmounted) return;
    host._pendingHarbingerInstall = { seat, avatar, marks, install, authPromptId };
    try {
      host.setState({
        harbingerCeremony: host.state.harbingerCeremony
          ? { ...host.state.harbingerCeremony, phase: 'pending_confirm' }
          : null,
      });
    } catch (err) {
      console.warn('[harbinger] pending-confirm setState failed:', err);
    }
  });
}

export function settleHarbingerCeremony(host) {
  const SETTLE_MS = 400;
  try { host.setState({ harbingerSweep: null }); } catch {}
  setTimeout(() => {
    if (host._unmounted) return;
    try { host.setState({ harbingerCeremony: null }); } catch {}
    // No local queue advance: a second Harbinger prompt pops server-side into
    // pendingChoice and the next fold animates it.
  }, SETTLE_MS);
}

// Auth fold: drive the ceremony off the server's harbinger pendingChoice
// (staged by server harbingerSetup.js at SET_FIRST_PLAYER). BOTH seats animate
// the identical staged marks (the prompt is public choreography); the owner
// gets Accept/Cancel (phase pending_confirm → chooseIntent), the opponent sees
// "awaiting their decision" and settles when the prompt clears. Returns true
// when `pc` is a harbinger prompt (the generic pickers must not render it).
// Reconnect-safe by construction: a Full re-folds the still-open prompt (fresh
// promptId tracking after a remount re-runs the sweep to the same marks); an
// accepted ceremony has no prompt and renders straight from the ledger fold.
export function foldHarbingerPromptFromMirror(host, pc) {
  const isHarb = !!pc && pc.promptKind === 'harbinger';
  if (!isHarb) {
    // The tracked prompt resolved server-side (accept/cancel committed, or the
    // queue advanced past it). Settle whatever ceremony state remains. The
    // non-owner waiting on the decision; the owner's optimistic settle already
    // ran and this is an idempotent no-op.
    if (host._harbingerPromptId) {
      host._harbingerPromptId = null;
      host._pendingHarbingerInstall = null;
      if (host.state.harbingerCeremony || host.state.harbingerSweep) {
        host._harbingerSweepCancel?.();
        host._harbingerSweepCancel = null;
        settleHarbingerCeremony(host);
      }
    }
    return false;
  }
  if (host._harbingerPromptId === pc.promptId) return true; // already animating it
  const grid = getGameGrid(host.state.spawnConfig);
  const { colPositions, rowPositions } = grid
    ? computeGridPositions(grid)
    : { colPositions: [], rowPositions: [] };
  const totalCols = colPositions.length - 1;
  const totalRows = rowPositions.length - 1;
  // Hold the ceremony while the first-player dice modal is still presenting
  // (the prompt rides the same diff as SET_FIRST_PLAYER) or the grid isn't
  // ready. No further diff is guaranteed, so re-poll off a short timer.
  if (host.state.showFirstPlayerDice || totalCols <= 0 || totalRows <= 0) {
    if (!host._harbingerFoldRetry) {
      host._harbingerFoldRetry = setTimeout(() => {
        host._harbingerFoldRetry = null;
        if (host._unmounted) return;
        foldHarbingerPromptFromMirror(host, host._authMirror?.getState?.()?.pendingChoice);
      }, 400);
    }
    return true;
  }
  // A new prompt replaced the tracked one in the same diff (mirror match: the
  // queue popped the second Harbinger straight into the slot). Reset first.
  host._harbingerSweepCancel?.();
  host._harbingerSweepCancel = null;
  host._pendingHarbingerInstall = null;
  host._harbingerPromptId = pc.promptId;

  const seatNum = pc.seat === 'p2' ? 2 : 1;
  // Solo: the pilot drives BOTH seats, so every ceremony is theirs to answer.
  // `_localSeat()` follows the piloted side there, so keying the decision on it
  // left the OTHER seat's ceremony stuck on "Awaiting their decision" with no
  // buttons and no way to advance (tabbing doesn't re-fold a tracked prompt).
  // The banner still names the seat rather than claiming "Your Harbinger" for
  // both, so `ownedByLocal` (title/pronoun) and `canDecide` (buttons) split.
  const isSolo = !!host.props?.isCasualSandbox;
  const ownedByLocal = !isSolo && pc.seat === host._localSeat?.();
  const canDecide = isSolo || ownedByLocal;
  const avatar = host._findAvatarForPlayer(seatNum) || null;
  host.setState({
    harbingerCeremony: {
      playerLabel: `P${seatNum}`,
      ownedByLocal,
      canDecide,
      portraitUrl: harbingerPortraitUrl(host, avatar),
      phase: 'rolling',
    },
    harbingerSweep: null,
  });
  animateHarbingerCeremony(host, {
    avatar,
    marks: pc.marks || [],
    totalCols,
    totalRows,
    install: canDecide,
    seat: seatNum,
    authPromptId: pc.promptId,
  });
  return true;
}

// The answering seat is the PROMPT's seat, never the viewer's. They coincide
// online (only the owner gets buttons), but the solo pilot answers a prompt
// addressed to the seat they are not currently piloting, and intentAuthz drops
// a choose reply whose envelope seat doesn't match pendingChoice.seat.
const seatKeyOf = (pending) => (pending.seat === 2 ? 'p2' : 'p1');

export function acceptHarbingerCeremony(host) {
  const pending = host._pendingHarbingerInstall;
  if (!pending) return;
  host._pendingHarbingerInstall = null;
  try { playUI(UI.CONFIRM); } catch {}
  // Auth: reply to the server prompt. ApplyModalResume ('harbinger_install')
  // installs the ledger entry, and the ledger fold renders the circles. Never
  // add the entry locally (the overwrite-style ledger fold would wipe it).
  if (pending.authPromptId) {
    if (pending.install) {
      host._sendMatchIntent?.(chooseIntent(pending.authPromptId, 'accept', seatKeyOf(pending)));
    }
    settleHarbingerCeremony(host);
    return;
  }
  if (pending.install) {
    try {
      const entry = buildHarbingerLedgerEntry(pending.avatar, pending.marks);
      host.effectLedger.add(entry);
    } catch (err) {
      console.warn('[harbinger] accept install failed:', err);
    }
  }
  settleHarbingerCeremony(host);
}

export function cancelHarbingerCeremony(host) {
  const pending = host._pendingHarbingerInstall;
  if (!pending) return;
  host._pendingHarbingerInstall = null;
  try { playUI(UI.CANCEL); } catch {}
  // Auth: reply 'cancel'. The server clears the prompt without installing.
  if (pending.authPromptId) {
    if (pending.install) {
      host._sendMatchIntent?.(chooseIntent(pending.authPromptId, 'cancel', seatKeyOf(pending)));
    }
    settleHarbingerCeremony(host);
    return;
  }
  settleHarbingerCeremony(host);
}

export function harbingerPortraitUrl(host, avatarCard) {
  if (!avatarCard) return null;
  const fullCard = host._getFullCard(avatarCard.cardId);
  return fullCard?.imageUrl || avatarCard.imageUrl || null;
}

// Per-frame sync of Harbinger summoning-circle decals. Mirrors the
// region-view tint pipeline: one transparent shader-shaded quad per
// marked cell, sitting just above the playmat and below the card
// layer. The source-of-truth for marks is the avatar's
// MODIFY_CAST_COST ledger entry (filter.cells); reading the ledger
// every frame handles session restore, MP sync, and avatar
// leave-play cleanup for free. When the entry disappears the
// meshes get reaped on the next tick.
//
// `uActive` dims marks whose discount has been consumed this turn
// so the player can see the once-per-turn gate at a glance.
export function syncHarbingerCircles(host) {
  if (!host.scene?.scene) return;
  const grid = getGameGrid(host.state.spawnConfig);

  const wanted = new Map();
  if (grid) {
    // Persistent marks from installed ledger entries. Dragon-filtered
    // cost mods (Caelestis, Dragonlord's Lair) render with their own
    // emerald palette so they never get mistaken for Harbinger marks;
    // everything else keeps the per-player gold / red.
    for (const entry of host.effectLedger.entries) {
      const marks = marksFromLedgerEntry(entry);
      if (!marks) continue;
      const isDragonPortal = String(entry.effect.args?.filter?.subtype || '').toLowerCase() === 'dragon';
      const playerKey = entry.sourceCardName === 'Town' ? 'town' : isDragonPortal ? 'dragon' : (entry.targetPlayer === 'p2' ? 'p2' : 'p1');
      const max = entry.effect.args?.max_uses_per_turn;
      const consumed = typeof max === 'number' && (entry.usesThisTurn || 0) >= max;
      for (const m of marks) {
        const key = `${entry.id}:${m.col},${m.row}`;
        wanted.set(key, { col: m.col, row: m.row, playerKey, consumed });
      }
    }
    // Slot-machine ceremony layers. `sweepCell` is the cell the RNG
    // is currently highlighting (replaced every ~70ms while a round
    // cycles); `lockedCells` are the picks that have already locked
    // in during the current Harbinger's ceremony. Both render the
    // same shader; sweep cells skip the spawn ramp via instant=true
    // so they're visible the moment they appear, since their mesh
    // is disposed before any slow ramp would complete.
    const sweep = host.state.harbingerSweep;
    if (sweep) {
      const playerKey = sweep.playerKey || 'p1';
      if (sweep.sweepCell) {
        const m = sweep.sweepCell;
        // Keyed by position, not by id, so a sweep landing on the
        // same cell two rounds in a row reuses the mesh rather than
        // flashing through dispose/create.
        const key = `sweep:${m.col},${m.row}`;
        wanted.set(key, { col: m.col, row: m.row, playerKey, consumed: false, instant: true });
      }
      for (const m of sweep.lockedCells || []) {
        const key = `locked:${m.col},${m.row}`;
        wanted.set(key, { col: m.col, row: m.row, playerKey, consumed: false, instant: false });
      }
    }
  }

  for (const [key, mesh] of host.harbingerCircleMeshes) {
    if (!wanted.has(key)) {
      host.scene.scene.remove(mesh);
      disposeHarbingerCircle(mesh);
      host.harbingerCircleMeshes.delete(key);
    }
  }

  if (wanted.size === 0) return;

  const nowSec = performance.now() * 0.001;
  const nowMs = performance.now();
  // v1.111. Pick renderOrder based on view mode. Surface view:
  // -90 (below every card so card art remains unobscured). Sub-
  // surface view: 1.5 (above the vanilla playmat overlay at
  // renderOrder=0.5. Otherwise the opaque subsurface skin paints
  // over the decal). Surface cards are hidden by
  // _applyRegionViewVisibility in subsurface, so the higher order
  // doesn't conflict with any in-view card.
  //
  // v1.75. Also keep the circles at the subsurface renderOrder
  // for the FULL duration of any active region-view transition.
  // Without this, an exit transition (subsurface→surface) flips
  // regionView to 'surface' immediately, dropping the circles to
  // -90. But the vanilla playmat overlay still has near-full
  // opacity during the first ~1s of the fade-out, so it paints
  // over the now-low-renderOrder circles and they appear to vanish
  // for a beat before re-emerging once the overlay finishes
  // fading. Pinning to 1.5 during the transition keeps them above
  // the overlay throughout; at rest the swap to -90 still happens.
  const subsurface = host.state.regionView === 'subsurface';
  const transitioning = !!host._viewTransition;
  const renderOrder = (subsurface || transitioning) ? 1.5 : -90;
  for (const [key, spec] of wanted) {
    let mesh = host.harbingerCircleMeshes.get(key);
    if (!mesh || mesh.userData?.playerKey !== spec.playerKey) {
      if (mesh) {
        host.scene.scene.remove(mesh);
        disposeHarbingerCircle(mesh);
      }
      mesh = createHarbingerCircle(spec.playerKey);
      host.scene.scene.add(mesh);
      host.harbingerCircleMeshes.set(key, mesh);
    }
    if (mesh.renderOrder !== renderOrder) mesh.renderOrder = renderOrder;
    const cellGeo = sampleCellGeometry(grid, spec.col, spec.row);
    if (cellGeo) updateHarbingerCircleGeometry(mesh, cellGeo);
    const uniforms = mesh.material.uniforms;
    if (uniforms?.uTime) uniforms.uTime.value = nowSec;
    if (uniforms?.uActive) {
      const target = spec.consumed ? 0.35 : 1.0;
      uniforms.uActive.value = uniforms.uActive.value + (target - uniforms.uActive.value) * 0.12;
    }
    if (uniforms?.uSpawn) {
      if (spec.instant) {
        uniforms.uSpawn.value = 1;
      } else {
        const spawnedAt = mesh.userData?._spawnedAt ?? nowMs;
        const elapsed = nowMs - spawnedAt;
        uniforms.uSpawn.value = Math.min(1, Math.max(0, elapsed / 360));
      }
    }
    // Summon flash decay. _flashStartedAt is stamped in
    // maybeFireHarbingerSummonFlash; ramp from 1 to 0 over 600ms
    // with a quadratic falloff so the surge feels punchy at the
    // start and tails off cleanly.
    if (uniforms?.uPlayFlash) {
      const flashStart = mesh.userData?._flashStartedAt ?? 0;
      if (flashStart > 0) {
        const flashElapsed = nowMs - flashStart;
        const FLASH_MS = 600;
        if (flashElapsed >= FLASH_MS) {
          uniforms.uPlayFlash.value = 0;
          mesh.userData._flashStartedAt = 0;
        } else {
          const t = flashElapsed / FLASH_MS;
          uniforms.uPlayFlash.value = (1.0 - t) * (1.0 - t);
        }
      } else {
        uniforms.uPlayFlash.value = 0;
      }
    }
  }
}

// Light the portal on the dropped cell if it's one of the cast
// player's Harbinger marks AND the card being cast is a Minion.
// Looks up every persistent ledger circle mesh keyed for this
// (col,row) under the controller's player key and stamps its
// `_flashStartedAt` so the render loop ramps uPlayFlash from 1→0.
// No-op when no matching mesh exists (e.g. the player just hit
// Cancel during the ceremony. No ledger entry, no decal).
export function maybeFireHarbingerSummonFlash(host, card, fullCard, dropCell) {
  if (!dropCell || !card || !fullCard) return;
  if (!countsAsMinion(fullCard)) return;
  const controller = card.rotated ? 'p2' : 'p1';
  const cellKeySuffix = `:${dropCell.col},${dropCell.row}`;
  const now = performance.now();
  for (const [key, mesh] of host.harbingerCircleMeshes) {
    if (!key.endsWith(cellKeySuffix)) continue;
    if (mesh.userData?.playerKey !== controller) continue;
    mesh.userData._flashStartedAt = now;
  }
}

export function disposeHarbingerCircles(host) {
  if (!host.harbingerCircleMeshes) return;
  for (const [, mesh] of host.harbingerCircleMeshes) {
    host.scene?.scene?.remove(mesh);
    disposeHarbingerCircle(mesh);
  }
  host.harbingerCircleMeshes.clear();
}
